import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import { useLocation } from 'react-router-dom';
import { getDefaultLanguage, getSupportedLanguages } from '../config/tenants';

function SEOHead({ seo, fallback = {}, robots }) {
  const { language } = useLanguage();
  const location = useLocation();
  const supportedLanguages = getSupportedLanguages();
  const defaultLanguage = getDefaultLanguage();

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const pathname = location.pathname;

  // Strip language prefix to get the base path for building alternate URLs
  const segments = pathname.split('/').filter(Boolean);
  const basePath = (supportedLanguages.length > 1 && segments.length > 0 && supportedLanguages.includes(segments[0]))
    ? (segments.length > 1 ? '/' + segments.slice(1).join('/') : '/')
    : pathname;

  // Default social-share image — every page renders an OG image even without a
  // product/category photo, so link previews on Facebook/X/LinkedIn/Slack are
  // never image-less. A page-specific image (e.g. ProductPage's first product
  // photo) still wins. Override the asset path per-tenant via
  // VITE_OG_DEFAULT_IMAGE without touching code.
  const DEFAULT_OG_IMAGE = import.meta.env.VITE_OG_DEFAULT_IMAGE || '/og-image.png';

  // Merge SEO data with fallbacks
  const title = seo?.title || fallback.title || 'PremiumStore - Multi-vendor Marketplace';
  const description = seo?.description || fallback.description || 'Compare prices and find the best deals from trusted vendors';
  const keywords = seo?.keywords || fallback.keywords || [];
  const canonicalUrl = seo?.canonicalUrl || fallback.canonicalUrl;
  const ogType = seo?.ogType || 'website';

  // Resolve og:image: page-specific > fallback > branded default.
  const rawOgImage = seo?.ogImage || fallback.ogImage || DEFAULT_OG_IMAGE;
  const usingDefaultOgImage = !(seo?.ogImage || fallback.ogImage);
  // OG/Twitter require an ABSOLUTE URL (http/https). Product photos already are,
  // but the default asset path is relative ("/og-image.png") — absolutize it
  // so scrapers accept it. (Default asset is 1200x630 — advertise its size.)
  const ogImage = rawOgImage && /^https?:\/\//i.test(rawOgImage)
    ? rawOgImage
    : `${origin}${rawOgImage.startsWith('/') ? '' : '/'}${rawOgImage}`;

  const keywordsString = Array.isArray(keywords) ? keywords.join(', ') : keywords;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywordsString && <meta name="keywords" content={keywordsString} />}
      <html lang={language} />

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="PremiumStore" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && usingDefaultOgImage && <meta property="og:image:width" content="1200" />}
      {ogImage && usingDefaultOgImage && <meta property="og:image:height" content="630" />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:locale" content={language === 'cs' ? 'cs_CZ' : 'en_US'} />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      {ogImage && <meta property="twitter:image" content={ogImage} />}

      {/* Hreflang alternate language tags — path-based /{lang}/ URLs */}
      {supportedLanguages.length > 1 && supportedLanguages.map((lang) => {
        const href = basePath === '/'
          ? `${origin}/${lang}`
          : `${origin}/${lang}${basePath}`;
        return <link key={lang} rel="alternate" hreflang={lang} href={href} />;
      })}
      {supportedLanguages.length > 1 && (
        <link
          rel="alternate"
          hreflang="x-default"
          href={basePath === '/'
            ? `${origin}/${defaultLanguage}`
            : `${origin}/${defaultLanguage}${basePath}`}
        />
      )}

      {/* Additional SEO / Accessibility */}
      <meta name="robots" content={robots || "index, follow"} />
      <meta name="language" content={language} />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="PremiumStore" />
      <meta name="color-scheme" content="light" />

      {/* Sitemap discovery */}
      <link rel="sitemap" type="application/xml" href="/sitemap.xml" />
    </Helmet>
  );
}

export default SEOHead;
